import { RliSdk, Widget } from '@rocketlane/rli-internal-sdk';
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { MARKETPLACE_SERVICE_BE_URL } from 'config/environment';
import { ExpenseEventsMap } from 'events/expenses';
import { ProjectEventsMap } from 'events/projects';
import { ResourceRequestEventsMap } from 'events/resource-request';
import { TaskEventsMap } from 'events/tasks';
import {
  useGetInstalledMarketplaceApps,
  useGetMarketplaceAuthToken,
} from 'marketplace/hooks/use-marketplace-hooks';
import { useWidgetCommunicationHandler } from 'marketplace/hooks/use-widget-communication-handler';
import { RlMarketplaceAppVersion } from 'marketplace/types/marketplace';

export const IframeWrapper = styled.div<{
  height?: string;
  width?: string;
}>`
  display: none;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const AddMarketplaceClientEventScripts: React.FC<{
  version: RlMarketplaceAppVersion;
}> = ({ version }) => {
  const { registerCallbacks, show } = useWidgetCommunicationHandler(
    version.appId,
    {
      isCustomerPortal: false,
      isLds: false,
    },
  );
  const iframeRef = useRef<Widget | null>(null);
  const { data: marketplaceAuthToken } = useGetMarketplaceAuthToken();

  useEffect(() => {
    if (iframeRef.current === null && marketplaceAuthToken?.token) {
      const clientEventsSrc = `${version.clientEvents
        ?.replaceAll('./', '/')
        ?.replaceAll('//', '/')
        ?.replaceAll('https:/', 'https://')
        ?.replaceAll('https:///', 'https://')}`;

      RliSdk?.registerWidget({
        appId: version.appId,
        apiKey: marketplaceAuthToken?.token,
        baseUrl: MARKETPLACE_SERVICE_BE_URL,
        id: `marketplace-client-events${version.appVersionId}`,
        src: clientEventsSrc,
        widgetIdentifier: `marketplace-client-events-${version.appId}-${version.appVersionId}`,
        events: {
          ...TaskEventsMap,
          ...ProjectEventsMap,
          ...ExpenseEventsMap,
          ...ResourceRequestEventsMap,
        },
        data: {
          GET_USER_DATA: 'user',
          // GET_USER_PROJECTS: 'user_projects',
          GET_USER_ACCOUNT: 'account',
        },
        parent: document.getElementById(
          `marketplace-client-events${version.appVersionId}`,
        ) as HTMLElement,
      }).then((widget) => {
        iframeRef.current = widget as Widget;
        registerCallbacks(widget as Widget);
      });
    }

    return () => {
      if (iframeRef.current) {
        RliSdk?.deRegisterWidget?.(iframeRef.current?.id);
        iframeRef.current = null;
      }
    };
  }, [version, registerCallbacks, show, marketplaceAuthToken?.token]);

  return (
    <IframeWrapper id={`marketplace-client-events${version.appVersionId}`} />
  );
};

export const AddMarketplaceClientEventScriptsForAllApps: React.FC = () => {
  const [
    enableMarketplaceClientEventScripts,
    setEnableMarketplaceClientEventScripts,
  ] = useState(false);
  const { data: installedMarketplaceApps = [] } =
    useGetInstalledMarketplaceApps(enableMarketplaceClientEventScripts);

  const appsWithClientEvents = useMemo(
    () =>
      installedMarketplaceApps.filter(
        (app) => app?.version?.clientEvents && !app?.installation?.disabled,
      ),
    [installedMarketplaceApps],
  );

  useEffect(() => {
    const timeOut = setTimeout(() => {
      setEnableMarketplaceClientEventScripts(true);
    }, 2000);

    return () => clearTimeout(timeOut);
  }, []);

  return (
    <>
      {appsWithClientEvents.map((app) => (
        <AddMarketplaceClientEventScripts
          key={app.version?.appVersionId ?? ''}
          version={app.version}
        />
      ))}
    </>
  );
};
